/** @jsx tsx */
import { dom, tsx } from "@dojo/framework/core/vdom";
import { WidgetBase } from "@dojo/framework/core/WidgetBase";
import * as _ from "lodash";
import { eventHandler } from "../../store/EventHandler";
import { ConnectionShapeContainer } from "../containers/ShapeContainer";
import { IApiSet } from "../injectors/ApiContext";
import { CanvasMode } from "../interfaces/model/enumerations/index";
import { ICanvasEditor, IPage, ITextStyles } from "../interfaces/model/index";
import { ShapeContainer } from "./../containers/ShapeContainer";
import { GroupShape } from "./GroupShape";
import { Picture } from "./Picture";
import { userIsDragging, isShapeObject, isPictureObject, isGroupShapeObject } from "../util";
/**
 * Page render properties
 */
export interface IPageRenderProps {
    /** Api object */
    api: IApiSet;

    /** Canvas editor options */
    canvasEditor: ICanvasEditor;

    /** Current mode that canvas is in */
    canvasMode: CanvasMode;

    /** Default text styles for shapes */
    defaultTextStyles: ITextStyles;

    /** Flag used for generating canvas snapshots */
    dontDownloadImages: boolean;

    /** Flag to indicate whether or not to emit onImagesLoaded event */
    emitImagesLoadedEvent: boolean;

    /** Selected element within a group. -1 if no such element */
    groupSelectionIndices: number[];

    /** type of objects to highlight */
    highlightType: string;

    /** List of attribute keys in the canvas that cannot be filled */
    impossiblePaths?: string[];

    /** Number of images in initial page objects, only counts if emitImagesLoadedEvent is true */
    imageCount: number;

    /** Current Canvas Page */
    page: IPage;

    /** Array of selected object indices. This will help shape to identify if they are selected or not. */
    selectedPageObjects: number[];

    /** Format painter text first character styles */
    textStyles: any;
}

/**
 * Canvas Editor.
 * Provides base component for rendering canvas pages
 */
export class CanvasPage extends WidgetBase<IPageRenderProps> {
	/** Handle key generated by requestAnimationFrame api. Useful for canceling request, if required. */
	private _broadCastHandle: undefined | number;

	/** images loaded count */
	private _imagesLoadedCount = 0;

	/** Canvas root editor id */
	private canvasEditorId = "canvas-editor-root";

	/** drag class */
	private dragClass = "csi-dnd-dragging-over";

	/** number of last content changed */
	private lastContentChangeNo: number;

	/** previous html string to be pushed to client via onContentChange emit event */
	private previousHTML: string = "";

	/** previous page objects JSON string to be compared with current page objects to check for change */
	private previousPageObjects: string = "";

	private canvasEditorNode = document.createElement("div");

	constructor() {
		super();
		// set to -1 so event first transaction is tracked.
		this.lastContentChangeNo = -1;
	}

	/**
     * Set the outline of the page to grey by removing primary drag over class
     */
	protected removePageDragOverClass(): void {
		const { canvasContainerEl } = this.properties.api;
		canvasContainerEl.classList.remove(this.dragClass);
	}

	/**
     * Add primary drag over class to back
     */
	protected addPageDragOverClass(): void {
		const { canvasContainerEl } = this.properties.api;
		canvasContainerEl.classList.add(this.dragClass);
	};

	/**
     * Widget lifecycle method that is called whenever a dom node that is associated with a HNode is updated.
     * Override this method to access the dom node.
     * @param element The dom node represented by the vdom node.
     */
	protected onElementUpdated(element: Element) {
		// If we have previous, undone handle then cancel animation frame request and create new one to broadcast
		// content change.
		if (this._broadCastHandle) {
			cancelAnimationFrame(this._broadCastHandle);
			this._broadCastHandle = undefined;
		}
		// Get the handle for animation frame request so as to cancel it if
		// we get another broadcast request before we broadcast current one.
		// Operation will not be marked as undoable during drag to avoid too
		// many undo operations being created (C8-10199).
		this._broadCastHandle = requestAnimationFrame(() => {
			const { page } = this.properties;
			const { pageObjects } = page;
			const currentPageObjects = JSON.stringify(pageObjects);
			const { outerHTML } = element;
			this._broadCastHandle = undefined;
			const undoable = this.lastContentChangeNo < page.contentChanges;
			if (undoable || (!userIsDragging() &&
				(this.previousHTML !== outerHTML ||
				currentPageObjects !== this.previousPageObjects)
			)) {
				eventHandler.emit("onContentChange", {
					pageHtml: outerHTML,
					pageId: page.id,
					undoable,
				});
				this.previousHTML = outerHTML;
				this.previousPageObjects = currentPageObjects;
			}
			this.lastContentChangeNo = page.contentChanges;
		});
	}

	/** handle onload events as they propagate up */
	private emitImagesLoadedEvent() {
		this._imagesLoadedCount++;
		if (this._imagesLoadedCount === this.properties.imageCount) {
			let emitMsg;
			if (this._imagesLoadedCount === 1) {
				emitMsg = "Canvas Image Loaded";
			} else {
				emitMsg = `All ${this._imagesLoadedCount} Canvas Images have Loaded`;
			}

			eventHandler.emit("onImagesLoaded", emitMsg);
		}
	}

	/**
	 * Override WidgetBase#render to produce a virtual DOM tree.
	 * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
	 */
	protected render() {
		const {
			addPageDragOverClass,
			removePageDragOverClass,
		} = this;

		const {
			api,
			canvasEditor,
			canvasMode,
			defaultTextStyles,
			dontDownloadImages,
			groupSelectionIndices,
			highlightType,
			impossiblePaths,
			page,
			selectedPageObjects,
		} = this.properties;

		const { canvasContainerEl } = api;

		const { pageObjects = [] } = page || {};
		const selectedPageId = page && page.id;
		const { resetGuides } = api.guideApi;

		const {
			adjustHighlight,
			selectObject,
			selectObjectById,
			setTextEditorInstance,
			setFormatPainterTextStyles,
			deselectAllObjects,
			setFirstCharStyle,
		} = api.selectionApi;

		const {
			translateObjects,
			applyFormatPainter,
			setShapeTextContent,
			setObjectExtends,
			setSrcRectRefExt,
			removeMigratedExtents,
		} = api.pageApi;

		const {
			editPageObject,
			setOperationInProgress,
		} = api.canvasApi;

		const partOfGroup = false;
		const depth = 1;
		const isMultiSelect = selectedPageObjects?.length > 1;
		requestAnimationFrame(() => this.onElementUpdated(this.canvasEditorNode));
		const pos = pageObjects.map((pageObject, key) => {
			const isSelected = selectedPageObjects.indexOf(key) > -1;
			if (isShapeObject(pageObject)) {
				return (
					<ShapeContainer
						addPageDragOverClass={addPageDragOverClass}
						adjustHighlight={adjustHighlight}
						applyFormatPainter={applyFormatPainter}
						canvasContainerEl={canvasContainerEl}
						canvasEditor={canvasEditor}
						canvasMode={canvasMode}
						defaultTextStyles={defaultTextStyles}
						depth={depth}
						deselectAllObjects={deselectAllObjects}
						dontDownloadImages={dontDownloadImages}
						editPageObject={editPageObject}
						emitImagesLoadedEvent={this.emitImagesLoadedEvent}
						highlightType={highlightType}
						isMultiSelect={isMultiSelect}
						isSelected={isSelected}
						key={key}
						pageObject={pageObject}
						partOfGroup={partOfGroup}
						removeMigratedExtents={removeMigratedExtents}
						removePageDragOverClass={removePageDragOverClass}
						resetGuides={resetGuides}
						selectObject={selectObject}
						selectObjectById={selectObjectById}
						selectedPageId={selectedPageId}
						setFirstCharStyle={setFirstCharStyle}
						setFormatPainterTextStyles={setFormatPainterTextStyles}
						setObjectExtends={setObjectExtends}
						setOperationInProgress={setOperationInProgress}
						setShapeTextContent={setShapeTextContent}
						setSrcRectRefExt={setSrcRectRefExt}
						setTextEditorInstance={setTextEditorInstance}
						translateObjects={translateObjects}
					/>
				);
			}

			if (isPictureObject(pageObject)) {
				return (
					<Picture
						addPageDragOverClass={addPageDragOverClass}
						applyFormatPainter={applyFormatPainter}
						canvasContainerEl={canvasContainerEl}
						canvasMode={canvasMode}
						depth={depth}
						editPageObject={editPageObject}
						isSelected={isSelected}
						key={key}
						pageObject={pageObject}
						partOfGroup={partOfGroup}
						removePageDragOverClass={removePageDragOverClass}
						resetGuides={resetGuides}
						selectObject={selectObject}
						selectObjectById={selectObjectById}
						setObjectExtends={setObjectExtends}
						translateObjects={translateObjects}
					/>
				);
			}

			if (isGroupShapeObject(pageObject)) {
				return pageObject.childObjects.length ? (
					<GroupShape
						addPageDragOverClass={addPageDragOverClass}
						adjustHighlight={adjustHighlight}
						applyFormatPainter={applyFormatPainter}
						canvasContainerEl={canvasContainerEl}
						canvasMode={canvasMode}
						defaultTextStyles={defaultTextStyles}
						depth={depth}
						deselectAllObjects={deselectAllObjects}
						dontDownloadImages={dontDownloadImages}
						editPageObject={editPageObject}
						emitImagesLoadedEvent={this.emitImagesLoadedEvent}
						groupSelectionIndices={isSelected ? groupSelectionIndices : []}
						highlightType={highlightType}
						impossiblePaths={impossiblePaths}
						isSelected={isSelected}
						key={key}
						pageObject={pageObject}
						partOfGroup={partOfGroup}
						removePageDragOverClass={removePageDragOverClass}
						resetGuides={resetGuides}
						selectObject={selectObject}
						selectObjectById={selectObjectById}
						selectedPageId={selectedPageId}
						setFirstCharStyle={setFirstCharStyle}
						setObjectExtends={setObjectExtends}
						setShapeTextContent={setShapeTextContent}
						setSrcRectRefExt={setSrcRectRefExt}
						setTextEditorInstance={setTextEditorInstance}
						translateObjects={translateObjects}
					/>
				) : null;
			}

			return (
				<ConnectionShapeContainer
					applyFormatPainter={applyFormatPainter}
					canvasMode={canvasMode}
					depth={depth}
					editPageObject={editPageObject}
					isSelected={isSelected}
					key={key}
					pageObject={pageObject}
					partOfGroup={partOfGroup}
					resetGuides={resetGuides}
					selectObject={selectObject}
					selectObjectById={selectObjectById}
					translateObjects={translateObjects}
				/>
			);
		});
		return (
			dom({
				node: this.canvasEditorNode,
				attrs: {
					id: this.canvasEditorId,
					xmlns: "http://www.w3.org/1999/xhtml",
				},
				props: {
					key: this.canvasEditorId,
					classes: "c8_canvas_page",
					styles: canvasMode === CanvasMode.EDIT_TEXT ? { userSelect: "all" } : { userSelect: "none" },
				},
			}, pos)
		);
	}
}

export default CanvasPage;
